import "./media.css";
import { api, ApiError } from "../../api";
import { errorBanner, loadingBlock, pill } from "../shared/blocks";
import { intentFor, peekIntent, resolveIntent } from "../shared/intent";
import { createLifecycle, isAbort } from "../shared/lifecycle";
import { h, tidy } from "../shared/safe";
import { busyButton, card, labelled, notice, select, textInput, whoText } from "../facts/kit";
import { fingerprint } from "../content/contentModel";
import { clientWorkspaceNav } from "../client/workspaceNav";
import {
  LIMITS_TEXT,
  RIGHTS_LABELS,
  RIGHTS_OPTIONS,
  MAX_IMAGE_BYTES,
  approvalLabel,
  describeMediaProblem,
  emptyUpload,
  fetchBytes,
  fileNotes,
  parseMediaList,
  parseMediaReply,
  parsePin,
  parseRevisions,
  sha8,
  sizeText,
  uploadBody,
  uploadProblems,
  type MediaProblem,
  type MediaRevision,
  type PinResult,
  type UploadDraft,
} from "./mediaModel";

// Media: #/clients/:id/media. Images for ONE client, with their rights and alt text, their revisions and what was approved. UPLOADING IS NOT
// APPROVAL: an uploaded image is not used anywhere until a person approves that exact revision, and only an approved revision can be pinned
// into a site. Every server string is text; the only things shown as images are the server's own verified bytes of an accepted raster type.

const enc = encodeURIComponent;

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error ?? new Error("The file could not be read."));
    r.onload = () => {
      const s = typeof r.result === "string" ? r.result : "";
      const i = s.indexOf(",");
      resolve(i >= 0 ? s.slice(i + 1) : "");
    };
    r.readAsDataURL(file);
  });
}

export async function clientMediaView(root: HTMLElement, params: Record<string, string>): Promise<void> {
  const shell = h("div", { class: "qf qf-cd fx md" });
  root.replaceChildren(shell);
  const clientId = typeof params.id === "string" ? params.id : "";
  if (clientId === "") return void shell.append(errorBanner("This page needs a client. Go back to the Fleet and open one."));
  const life = createLifecycle(shell);
  const base = `/v1/clients/${enc(clientId)}/media`;
  const urls = new Set<string>();
  const makeUrl = (b: Blob) => {
    const u = URL.createObjectURL(b);
    urls.add(u);
    return u;
  };
  const revokeAll = () => {
    urls.forEach((u) => URL.revokeObjectURL(u));
    urls.clear();
  };
  window.addEventListener("hashchange", () => {
    if (!shell.isConnected) revokeAll();
  });

  shell.append(
    h("div", { class: "topbar" }, h("h1", null, "Media"), h("div", { class: "btn-group" }, h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}` }, "← Client"), h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}/site` }, "Site kit"))),
    clientWorkspaceNav(clientId, "Images"),
    h("div", { class: "ct-banner", role: "note", id: "md-banner" }, h("strong", null, "Uploading is not approval. "), "An uploaded image is only stored. It is not used on any site until you approve that exact revision, and a site can pin only approved revisions. Alt text and rights are recorded as you give them; CloudPush never writes or guesses them."),
  );

  // ---- upload form (placed once and never moved) ---------------------------------------------------------------------------------------
  let draft: UploadDraft = emptyUpload();
  let file: File | null = null;
  let previewUrl: string | null = null;
  const result = h("div", { id: "md-upload-result", "aria-live": "polite" });
  const fileNote = h("div", { class: "fx-help", id: "md-file-note", "aria-live": "polite" });
  const previewBox = h("div", { class: "md-preview", id: "md-preview" });
  const errors: Record<string, string> = {};
  const form = h("form", { class: "fx-form", novalidate: true, id: "md-form", "aria-label": "Upload an image" });
  const fileInput = h("input", { type: "file", id: "md-file", accept: "image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp,.svg", dataset: { field: "file" } }) as HTMLInputElement;
  const altInput = textInput("", { name: "alt", onInput: (v) => { draft.alt = v; delete errors.alt; sync(); } });
  const decorative = h("input", { type: "checkbox", id: "md-decorative", dataset: { field: "decorative" } }) as HTMLInputElement;
  const rightsSel = select(RIGHTS_OPTIONS.map((r): [string, string] => [r, RIGHTS_LABELS[r]]), "client_owned", { name: "rights", onChange: (v) => void (draft.rights = v) });
  const noteInput = textInput("", { name: "rightsNote", onInput: (v) => void (draft.rightsNote = v) });
  const extendSel = select([["", "A new image"]], "", { name: "extend", onChange: (v) => void (draft.extend = v) });
  extendSel.id = "md-extend";

  function sync() {
    for (const name of ["file", "alt", "rights"]) {
      const input = form.querySelector<HTMLElement>(`[data-field="${name}"]`);
      const wrap = input?.closest(".fx-field");
      wrap?.querySelector(".fx-error")?.remove();
      input?.removeAttribute("aria-invalid");
      if (errors[name] && wrap) {
        wrap.append(h("div", { class: "fx-error", role: "alert" }, errors[name]));
        input?.setAttribute("aria-invalid", "true");
      }
    }
    altInput.disabled = draft.decorative;
    const altWrap = altInput.closest(".fx-field") as HTMLElement | null;
    altWrap?.classList.toggle("md-dim", draft.decorative);
  }

  fileInput.addEventListener("change", () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      urls.delete(previewUrl);
      previewUrl = null;
    }
    previewBox.replaceChildren();
    file = fileInput.files?.[0] ?? null;
    delete errors.file;
    result.replaceChildren();
    if (!file) {
      draft.filename = "";
      draft.size = 0;
      fileNote.textContent = "";
      return sync();
    }
    draft.filename = file.name;
    draft.size = file.size;
    draft.mimeGuess = file.type;
    const notes = fileNotes(file);
    fileNote.replaceChildren(h("div", null, `${tidy(file.name, 120)} · ${file.type || "type not reported"} · ${sizeText(file.size)}`), ...(notes.block ? [h("div", { class: "fx-error", role: "alert", dataset: { fileBlock: "true" } }, notes.block)] : []), ...(notes.warn ? [h("div", { class: "md-warn", dataset: { fileWarn: "true" } }, notes.warn)] : []));
    if (notes.block) {
      errors.file = notes.block;
      file = null;
      fileInput.value = "";
      draft.filename = "";
      draft.size = 0;
    } else if (/^image\/(png|jpeg|webp)$/.test(file.type)) {
      // a client-side preview of the chosen file only (nothing has been sent); never SVG, never a file that is not an accepted raster type
      previewUrl = makeUrl(file);
      const img = h("img", { alt: "Preview of the chosen file (not yet uploaded)", class: "md-thumb", dataset: { preview: "true" } }) as HTMLImageElement;
      img.src = previewUrl; // an object URL this page just created for the chosen file (h() allows only web addresses)
      previewBox.replaceChildren(img, h("div", { class: "fx-help" }, "Preview of the chosen file. Nothing has been uploaded yet."));
    }
    sync();
  });
  decorative.addEventListener("change", () => {
    draft.decorative = decorative.checked;
    if (draft.decorative) delete errors.alt;
    sync();
  });

  const upload = busyButton({ label: "Upload image (not approved)", busyLabel: "Uploading…", id: "md-upload", type: "submit", onClick: () => uploadIt() });
  form.onsubmit = (e) => {
    e.preventDefault();
    void upload.click();
  };
  form.append(
    h(
      "div",
      { class: "fx-grid" },
      labelled("Image file", fileInput, { help: LIMITS_TEXT, wide: true }),
      labelled("Alt text (describes the image for people who cannot see it)", altInput, { wide: true }),
      h("div", { class: "fx-field fx-field--wide" }, h("label", { for: "md-decorative" }, decorative, " Decorative: it carries no meaning, so no alt text is needed")),
      labelled("Rights", rightsSel),
      labelled("Rights note (optional: who photographed it, licence number…)", noteInput),
      labelled("Add as", extendSel, { help: "A new revision of an existing image keeps the old revisions and their approvals; the new one starts NOT approved." }),
    ),
    fileNote,
    previewBox,
    h("div", { class: "btn-group" }, upload),
    result,
  );

  async function uploadIt() {
    result.replaceChildren();
    for (const k of Object.keys(errors)) delete errors[k];
    const probs = uploadProblems(draft);
    if (!file || probs.length) {
      for (const p of probs) errors[p.field] = p.message;
      sync();
      result.replaceChildren(notice("bad", "Fix the highlighted fields first. Nothing was sent."));
      form.querySelector<HTMLElement>(`[data-field="${probs[0]?.field ?? "file"}"]`)?.focus();
      return;
    }
    let b64: string;
    try {
      b64 = await readBase64(file);
    } catch {
      result.replaceChildren(notice("bad", "The file could not be read in the browser. Nothing was sent."));
      return;
    }
    // one intention, one key: the same file with the same details resent after an unreadable answer reuses its key (the server replays)
    const scope = `media|${clientId}|${fingerprint({ n: draft.filename, s: draft.size, m: file.lastModified, a: draft.alt, d: draft.decorative, r: draft.rights, rn: draft.rightsNote, x: draft.extend })}`;
    const intent = intentFor(scope);
    try {
      const reply = parseMediaReply(await api<unknown>(base, { method: "POST", body: JSON.stringify(uploadBody(draft, b64, intent.key)) }));
      if (!life.alive()) return;
      if (!reply) {
        result.replaceChildren(notice("warn", "The server replied, but not with an image record this page recognises, so it is not known whether it was stored. Check the list below; uploading the same file again repeats the same request."));
        return;
      }
      resolveIntent(scope);
      const m = reply.media;
      draft = emptyUpload();
      file = null;
      fileInput.value = "";
      decorative.checked = false;
      altInput.value = "";
      noteInput.value = "";
      extendSel.value = "";
      previewBox.replaceChildren();
      fileNote.replaceChildren();
      sync();
      result.replaceChildren(notice("ok", `${reply.replayed ? "This upload was already stored: showing the original. " : "Uploaded. "}Revision ${m.revision} of "${m.filename}" is NOT approved: it cannot be used on a site until you approve it.`));
      await loadList();
      if (open.has(m.mediaId)) await loadRevisions(m.mediaId); // an open revisions panel must show the revision just added
      listHost.querySelector<HTMLElement>(`[data-media-id="${m.mediaId}"] h3`)?.setAttribute("tabindex", "-1");
      listHost.querySelector<HTMLElement>(`[data-media-id="${m.mediaId}"] h3`)?.focus();
    } catch (e) {
      if (!life.alive() || isAbort(e)) return;
      const p = describeMediaProblem(e, "Not uploaded");
      if (p.definite) resolveIntent(scope);
      showProblem(p, result);
      if (!p.definite && peekIntent(scope)) result.append(h("p", { class: "muted" }, "Uploading the same file again repeats the same request with the same key, so it cannot store a second copy."));
    }
  }

  function showProblem(p: MediaProblem, into: HTMLElement) {
    into.replaceChildren(notice(p.kind === "network" || p.kind === "server" ? "warn" : "bad", p.message, p.fields.map((f) => (f.field ? `${f.field}: ${f.message}` : f.message))));
  }

  // ---- the list ---------------------------------------------------------------------------------------------------------------------
  let items: MediaRevision[] | null = null;
  let loadError: string | null = null;
  let next: string | null = null;
  let flash: HTMLElement | null = null;
  const flashHost = h("div", { "aria-live": "polite", id: "md-flash" });
  const listHost = h("div", { id: "md-list" });
  const revisions = new Map<string, MediaRevision[] | "loading" | "error">();
  const open = new Set<string>();
  const thumbs = new Map<string, string | "none">();
  /** The revision whose approve / withdraw confirmation is open. */
  let confirming: { revisionId: string; state: "approved" | "withdrawn"; problem: MediaProblem | null } | null = null;
  const pins = new Map<string, { result?: PinResult; problem?: MediaProblem }>();
  let seq = 0;

  async function loadList() {
    const mine = ++seq;
    try {
      const r = parseMediaList(await api<unknown>(`${base}?limit=50`));
      if (mine !== seq || !life.alive()) return;
      items = r?.items ?? null;
      next = r?.next ?? null;
      loadError = r ? null : "The server sent a list this page does not understand.";
      extendSel.replaceChildren(h("option", { value: "" }, "A new image"), ...(items ?? []).map((m) => h("option", { value: m.mediaId }, `A new revision of ${tidy(m.filename, 60)}`)));
      extendSel.value = draft.extend;
    } catch (e) {
      if (mine !== seq || !life.alive() || isAbort(e)) return;
      loadError = e instanceof ApiError ? (e.status === 404 ? "This client was not found." : e.status === 503 ? "The server has no object storage configured, so media is unavailable here." : `The media could not be loaded (HTTP ${e.status}).`) : "Couldn't reach the server.";
    }
    paintList();
    if (items) for (const m of items) void loadThumb(m);
  }

  async function loadThumb(m: MediaRevision) {
    if (thumbs.has(m.revisionId) || m.byteSize === null || m.byteSize > 2 * 1024 * 1024) return;
    thumbs.set(m.revisionId, "none");
    const blob = await fetchBytes(clientId, m.mediaId, m.revisionId);
    if (!life.alive()) return;
    if (blob) thumbs.set(m.revisionId, makeUrl(blob));
    paintList();
  }

  async function loadRevisions(mediaId: string) {
    revisions.set(mediaId, "loading");
    paintList();
    try {
      const r = parseRevisions(await api<unknown>(`${base}/${enc(mediaId)}/revisions`));
      if (!life.alive()) return;
      revisions.set(mediaId, r ?? "error");
    } catch (e) {
      if (!life.alive() || isAbort(e)) return;
      revisions.set(mediaId, "error");
    }
    paintList();
    const got = revisions.get(mediaId);
    if (Array.isArray(got)) for (const r of got) void loadThumb(r);
  }

  function revisionRow(m: MediaRevision, latest: boolean): HTMLElement {
    const st = approvalLabel(m.approval.state);
    const li = h(
      "li",
      { class: "md-rev", dataset: { revisionId: m.revisionId, revision: String(m.revision), approval: m.approval.state } },
      h("div", { class: "fx-unr-head" }, h("strong", null, `Revision ${m.revision}${latest ? " (latest)" : ""}`), pill(st.label, st.tone), h("span", { class: "muted", title: m.sha256 }, `sha256 ${sha8(m.sha256)}`), h("span", { class: "muted" }, `${m.width ?? "?"}×${m.height ?? "?"} · ${sizeText(m.byteSize)}`)),
      h("div", { class: "muted fx-small" }, `${m.mime} · imported by ${whoText(m.importedBy)}${m.createdAt ? ` · ${m.createdAt.text}` : ""}`),
      h("div", { class: "fx-small", dataset: { alt: "true" } }, m.decorative ? "Decorative (no alt text needed)." : `Alt text: “${m.alt}”`),
      h("div", { class: "muted fx-small" }, `Rights: ${RIGHTS_LABELS[m.rights] ?? m.rights}${m.rightsNote ? ` · ${m.rightsNote}` : ""}`),
      m.generation ? h("details",null,h("summary",null,"Generated illustration provenance"),h("p",null,`Illustration generated with ${m.generation.model}. It is not documentary evidence of real client work.`),h("p",null,m.generation.prompt),m.generation.revisedPrompt?h("p",null,m.generation.revisedPrompt):null) : null,
      m.approval.state !== "uploaded" && m.approval.state !== "unknown" ? h("div", { class: "muted fx-small" }, `${m.approval.state === "approved" ? "Approved" : "Withdrawn"} by ${whoText(m.approval.approvedBy)}${m.approval.at ? ` · ${m.approval.at.text}` : ""}`) : null,
    );
    const pin = pins.get(m.revisionId);
    if (confirming?.revisionId === m.revisionId) li.append(confirmBox(m));
    else {
      const actions = h("div", { class: "btn-group md-actions" });
      if (m.approval.state !== "approved") actions.append(h("button", { type: "button", dataset: { action: "approve" }, "aria-label": `Approve revision ${m.revision} of ${tidy(m.filename, 60)}`, on: { click: () => { confirming = { revisionId: m.revisionId, state: "approved", problem: null }; flash = null; paintList(); listHost.querySelector<HTMLElement>(`[data-revision-id="${m.revisionId}"] [data-confirm]`)?.focus(); } } }, "Approve this revision…"));
      if (m.approval.state === "approved") actions.append(h("button", { type: "button", class: "secondary", dataset: { action: "withdraw" }, "aria-label": `Withdraw approval of revision ${m.revision} of ${tidy(m.filename, 60)}`, on: { click: () => { confirming = { revisionId: m.revisionId, state: "withdrawn", problem: null }; flash = null; paintList(); listHost.querySelector<HTMLElement>(`[data-revision-id="${m.revisionId}"] [data-confirm]`)?.focus(); } } }, "Withdraw approval…"));
      actions.append(h("button", { type: "button", class: "secondary", dataset: { action: "pin" }, "aria-label": `Check that revision ${m.revision} of ${tidy(m.filename, 60)} can be pinned`, on: { click: () => void pinIt(m) } }, "Pin check"));
      li.append(actions);
    }
    if (pin?.result) li.append(h("div", { class: "md-pin md-pin--ok", role: "status", dataset: { pin: "ok" } }, `Pin check passed: this exact revision (sha256 ${sha8(pin.result.checksum)}${pin.result.path ? `, published path ${pin.result.path}` : ""}) is approved by ${whoText(pin.result.approvedBy)} and can be pinned into a site revision. This only checks; nothing is published.`));
    if (pin?.problem) li.append(h("div", { class: "md-pin md-pin--bad", role: "alert", dataset: { pin: "refused" } }, pin.problem.message, ...pin.problem.fields.map((f) => h("div", null, f.message))));
    return li;
  }

  function confirmBox(m: MediaRevision): HTMLElement {
    const c = confirming!;
    const approve = c.state === "approved";
    const go = busyButton({
      label: approve ? "Confirm: approve this revision" : "Confirm: withdraw approval",
      busyLabel: approve ? "Approving…" : "Withdrawing…",
      id: `md-confirm-${m.revisionId}`,
      onClick: async () => {
        const scope = `mediaapproval|${m.revisionId}|${c.state}`;
        const intent = intentFor(scope);
        try {
          const reply = parseMediaReply(await api<unknown>(`${base}/${enc(m.mediaId)}/revisions/${enc(m.revisionId)}/approval`, { method: "POST", body: JSON.stringify({ state: c.state, idempotencyKey: intent.key }) }));
          if (!life.alive()) return;
          resolveIntent(scope);
          confirming = null;
          pins.delete(m.revisionId);
          flash = notice("ok", approve ? `Revision ${m.revision} is approved (by the dashboard). It can now be pinned into a site; nothing is published.` : `Approval of revision ${m.revision} is withdrawn. It can no longer be pinned.`);
          void reply;
          await Promise.all([loadList(), revisions.has(m.mediaId) ? loadRevisions(m.mediaId) : Promise.resolve()]);
        } catch (e) {
          if (!life.alive() || isAbort(e)) return;
          const p = describeMediaProblem(e, approve ? "Not approved" : "Not withdrawn");
          if (p.definite) resolveIntent(scope);
          confirming = { ...c, problem: p };
          paintList();
        }
      },
    });
    go.dataset.confirm = "true";
    return h(
      "div",
      { class: "fx-promote", role: "group", "aria-label": `${approve ? "Approve" : "Withdraw"} revision ${m.revision}` },
      h("strong", null, approve ? "Approve this exact revision?" : "Withdraw approval of this revision?"),
      h("p", null, approve ? `You are approving revision ${m.revision} of “${tidy(m.filename, 80)}” (sha256 ${sha8(m.sha256)}, ${m.width ?? "?"}×${m.height ?? "?"}), with the alt text and rights shown above. A new upload of the same image is a new revision and starts NOT approved.` : "A site cannot pin this revision afterwards. Sites already built from it keep the exact bytes they were built with."),
      ...(c.problem ? [notice(c.problem.kind === "network" || c.problem.kind === "server" ? "warn" : "bad", c.problem.message, c.problem.fields.map((f) => f.message))] : []),
      h("div", { class: "btn-group" }, go, h("button", { type: "button", class: "secondary", on: { click: () => { confirming = null; paintList(); } } }, "Cancel")),
    );
  }

  async function pinIt(m: MediaRevision) {
    pins.delete(m.revisionId);
    try {
      const pin = parsePin(await api<unknown>(`${base}/${enc(m.mediaId)}/revisions/${enc(m.revisionId)}/pin`, { method: "POST", body: JSON.stringify({}) }));
      if (!life.alive()) return;
      pins.set(m.revisionId, pin ? { result: pin } : { problem: { kind: "other", message: "The server replied, but not with a pin this page recognises.", fields: [], definite: false } });
    } catch (e) {
      if (!life.alive() || isAbort(e)) return;
      pins.set(m.revisionId, { problem: describeMediaProblem(e, "Cannot be pinned") });
    }
    paintList();
    listHost.querySelector<HTMLElement>(`[data-revision-id="${m.revisionId}"] [data-pin]`)?.setAttribute("tabindex", "-1");
  }

  /** An <img> of the server's verified bytes (an object URL this page created); never any other source. */
  function thumbImg(url: string, m: MediaRevision): HTMLElement {
    const img = h("img", { alt: m.decorative ? "" : m.alt, class: "md-thumb", dataset: { thumb: "true" } }) as HTMLImageElement;
    if (urls.has(url)) img.src = url;
    return img;
  }

  function itemCard(m: MediaRevision): HTMLElement {
    const t = thumbs.get(m.revisionId);
    const isOpen = open.has(m.mediaId);
    const revs = revisions.get(m.mediaId);
    const li = h(
      "li",
      { class: "md-item", dataset: { mediaId: m.mediaId, approval: m.approval.state } },
      h(
        "div",
        { class: "md-item__row" },
        typeof t === "string" && t !== "none" ? thumbImg(t, m) : h("div", { class: "md-thumb md-thumb--none", "aria-hidden": "true" }, "no preview"),
        h("div", { class: "md-item__body" }, h("h3", null, m.filename), revisionRowSummary(m)),
      ),
      h("button", { type: "button", class: "secondary", id: `md-toggle-${m.mediaId}`, "aria-expanded": String(isOpen), on: { click: () => { if (isOpen) open.delete(m.mediaId); else { open.add(m.mediaId); void loadRevisions(m.mediaId); } paintList(); } } }, isOpen ? "Hide revisions" : `Show revisions (${m.revisionCount ?? 1})`),
    );
    if (isOpen) {
      li.append(
        revs === "loading" || revs === undefined ? loadingBlock("Loading revisions", 2) : revs === "error" ? h("div", { class: "error-banner", role: "alert" }, "The revisions could not be loaded.") : h("ol", { class: "md-revs", "aria-label": `Revisions of ${tidy(m.filename, 80)}` }, ...[...revs].reverse().map((r, i) => revisionRow(r, i === 0))),
      );
    }
    return li;
  }
  function revisionRowSummary(m: MediaRevision): HTMLElement {
    const st = approvalLabel(m.approval.state);
    return h(
      "div",
      null,
      h("div", { class: "fx-unr-head" }, pill(st.label, st.tone), h("span", { class: "muted" }, `latest: revision ${m.revision}`), h("span", { class: "muted", title: m.sha256 }, `sha256 ${sha8(m.sha256)}`), h("span", { class: "muted" }, `${m.width ?? "?"}×${m.height ?? "?"} · ${sizeText(m.byteSize)}`)),
      h("div", { class: "fx-small" }, m.decorative ? "Decorative." : `Alt text: “${m.alt}”`),
      h("div", { class: "muted fx-small" }, `Rights: ${RIGHTS_LABELS[m.rights] ?? m.rights}${m.rightsNote ? ` · ${m.rightsNote}` : ""}`),
    );
  }

  function paintList() {
    flashHost.replaceChildren(...(flash ? [flash] : []));
    listHost.replaceChildren(
      loadError
        ? h("div", { class: "error-banner", role: "alert" }, loadError, " ", h("button", { type: "button", class: "secondary", on: { click: () => void loadList() } }, "Try again"))
        : items === null
          ? loadingBlock("Loading media", 3)
          : items.length === 0
            ? h("p", { class: "muted" }, "No images yet. Upload the first one above.")
            : h("ul", { class: "md-list", "aria-label": "Images" }, ...items.map(itemCard)),
      ...(next ? [h("p", { class: "muted" }, "More images exist than are listed here (the first 50 are shown).")] : []),
    );
  }

  shell.append(card("md-upload-card", "Upload an image", h("p", { class: "muted" }, `Limits: ${LIMITS_TEXT}`), form), card("md-images", "This client's images", flashHost, listHost));
  sync();
  paintList();
  void MAX_IMAGE_BYTES;
  await loadList();
}
